import { useApolloClient } from "@apollo/client";
import { getDataOrNull } from "@msys/common";
import {
  DebouncedSearchInput,
  ListHeader,
  ListHeaderActiveFilters,
  ListHeaderActiveFiltersActionButton,
  ModalOpenButton,
  Tabs,
} from "@msys/ui";
import { Typography } from "@mui/material";
import { useTranslate } from "@tolgee/react";
import React from "react";
import { OrganisationQuoteTemplatesFilters } from "../../../../clients/graphqlTypes.js";
import { RestrictedByCapabilityWithDebug } from "../../../auth/RestrictedByCapability.js";
import { RestrictedByOrganisationPermissionWithDebug } from "../../../auth/RestrictedByOrganisationPermission.js";
import { SwitchCollectionViewButton } from "../../../commons/button/SwitchCollectionViewButton.js";
import { FilterButton } from "../../../commons/filters/FilterButton.js";
import {
  CollectionView,
  useCollectionViewWithMobile,
} from "../../../commons/hooks/useCollectionView.js";
import { useStateWithUrlParam } from "../../../commons/hooks/useStateWithUrlParam.js";
import { Page, PageTopbarItem } from "../../../commons/layout/Page.js";
import { PageContainer } from "../../../commons/layout/PageContainer.js";
import { ImportTemplatesIconButton } from "../../../features/templates/buttons/ImportTemplatesButton.js";
import { CreateQuoteTemplateButton } from "../../../features/templates/quote/buttons/CreateTemplateQuoteButton.js";
import { TemplateQuotesFilterModal } from "../../../features/templates/quote/TemplatesQuoteFilterModal.js";
import { useTemplatesQueryVariablesInUrl } from "../../../features/templates/quote/useTemplatesQueryVariables.js";
import { TemplateDocumentsList } from "../../../features/templates/TemplateDocumentsList.js";
import { useTemplateDocumentsActiveFilterChips } from "../../../features/templates/useTemplateDocumentsActiveFilterChips.js";
import { useTemplateDocumentsListBulkActions } from "../../../features/templates/useTemplateDocumentsListBulkActions.js";
import {
  TemplateQuotes_TemplateFragment,
  TemplateQuotesQueryVariables,
  useTemplateQuotesQuery,
} from "./TemplateQuotes.generated.js";

type TemplatesCollectionView = Extract<
  CollectionView,
  "table" | "list" | "gallery"
>;
const ALLOWED_VIEWS: TemplatesCollectionView[] = ["table", "list", "gallery"];

type SectionValue = "MY" | "MEISTER_STORE";
type QuickFilterValue =
  | "MY_ALL"
  | "MY_DRAFT"
  | "MY_PRIVATE"
  | "MY_PROTECTED"
  | "MY_PUBLIC"
  | "MEISTER_STORE_ALL"
  | "MEISTER_STORE_SUBSCRIBED";
const QUICK_FILTER_TO_SECTION_MAP: Record<QuickFilterValue, SectionValue> = {
  MY_ALL: "MY",
  MY_DRAFT: "MY",
  MY_PRIVATE: "MY",
  MY_PROTECTED: "MY",
  MY_PUBLIC: "MY",
  MEISTER_STORE_ALL: "MEISTER_STORE",
  MEISTER_STORE_SUBSCRIBED: "MEISTER_STORE",
};
const SECTION_TO_DEFAULT_QUICK_FILTER: Record<SectionValue, QuickFilterValue> =
  {
    MY: "MY_ALL",
    MEISTER_STORE: "MEISTER_STORE_ALL",
  };

type Filters = OrganisationQuoteTemplatesFilters;

interface Props {
  submenuItems: PageTopbarItem[];
  section: SectionValue;
  pathToDocList: string;
}

export const TemplateQuotes = ({
  submenuItems,
  section,
  pathToDocList,
}: Props) => {
  const { t } = useTranslate(["Templates", "Global", "TemplatesSharing"]);

  const quickFilterSettings: Record<
    QuickFilterValue,
    { label: string; values: Filters; visible?: boolean }
  > = React.useMemo(
    () => ({
      MY_ALL: {
        label: t("All", { ns: "Global" }),
        values: {
          sources: ["mine_published", "mine_unpublished"],
        },
      },
      MY_DRAFT: {
        label: t("Draft", { ns: "Templates" }),
        values: {
          sources: ["mine_unpublished"],
        },
      },
      MY_PRIVATE: {
        label: t("Private", { ns: "TemplatesSharing" }),
        values: {
          sources: ["mine_published"],
          mineShareStatus: "PRIVATE",
        },
      },
      MY_PROTECTED: {
        label: t("Protected", { ns: "TemplatesSharing" }),
        values: {
          sources: ["mine_published"],
          mineShareStatus: "PROTECTED",
        },
      },
      MY_PUBLIC: {
        label: t("Public", { ns: "TemplatesSharing" }),
        values: {
          sources: ["mine_published"],
          mineShareStatus: "PUBLIC",
        },
      },
      MEISTER_STORE_ALL: {
        label: t("All", { ns: "Global" }),
        values: {
          sources: ["subscribable", "subscribed"],
        },
      },
      MEISTER_STORE_SUBSCRIBED: {
        label: t("Subscribed", { ns: "Templates" }),
        values: {
          sources: ["subscribed"],
        },
      },
    }),
    [t]
  );

  const sectionTitles = React.useMemo(
    (): Record<SectionValue, string> => ({
      MY: t("My Templates", { ns: "Templates" }),
      MEISTER_STORE: t("Meister Store", { ns: "Templates" }),
    }),
    [t]
  );

  const {
    paginationModel,
    setPaginationModel,
    filters,
    setFilters,
    resetFilters,
    sorting,
    setSorting,
    queryBaseVariables,
    toRemoveParams,
    searchTerm,
    setSearchTerm,
  } = useTemplatesQueryVariablesInUrl<Filters>({
    initialFilters: {},
  });

  const [activeView, setActiveView] =
    useCollectionViewWithMobile<TemplatesCollectionView>(
      "templates",
      "table",
      "list"
    );

  const [quickFilter, setQuickFilter] = useStateWithUrlParam<QuickFilterValue>(
    "quickfilter",
    SECTION_TO_DEFAULT_QUICK_FILTER[section],
    toRemoveParams
  );

  const activeQuickFilterOptions = React.useMemo<
    { label: string; value: QuickFilterValue }[]
  >(
    () =>
      Object.entries(quickFilterSettings)
        .filter(([, value]) => value?.visible !== false)
        .map(([key, value]) => ({
          value: key as QuickFilterValue,
          label: value.label,
        }))
        .filter(o => QUICK_FILTER_TO_SECTION_MAP[o.value] === section),
    [quickFilterSettings, section]
  );

  const variables: TemplateQuotesQueryVariables = {
    ...queryBaseVariables,
    sorting,
    searchTerm,
    filters: {
      ...quickFilterSettings[quickFilter].values,
      ...filters,
      createdAfter: filters?.createdAfter?.format("YYYY-MM-DD"),
      createdBefore: filters?.createdBefore?.format("YYYY-MM-DD"),
    },
  };

  const client = useApolloClient();
  const query = useTemplateQuotesQuery({
    client,
    variables,
  });

  const items = getDataOrNull(
    (query.data ?? query.previousData)?.quoteTemplates
  )?.edges.map(e => e.node);
  const totalCount =
    getDataOrNull((query.data ?? query.previousData)?.quoteTemplates)
      ?.totalCount ?? 0;

  const [selectedItems, setSelectedItems] = React.useState<
    TemplateQuotes_TemplateFragment[]
  >([]);
  const resetSelectedItems = React.useCallback(() => setSelectedItems([]), []);
  React.useEffect(() => {
    resetSelectedItems();
  }, [section, quickFilter, resetSelectedItems]);

  const { actionButtons } = useTemplateDocumentsListBulkActions(
    quickFilter,
    selectedItems,
    resetSelectedItems
  );

  const createButton = (
    <RestrictedByOrganisationPermissionWithDebug permission="MANAGE_TEMPLATE">
      <CreateQuoteTemplateButton pathToDocList={pathToDocList} />
    </RestrictedByOrganisationPermissionWithDebug>
  );

  const importButton = (
    <RestrictedByCapabilityWithDebug capability="TEMPLATE_IMPORT_EXPORT">
      <RestrictedByOrganisationPermissionWithDebug permission="MANAGE_TEMPLATE">
        <ImportTemplatesIconButton />
      </RestrictedByOrganisationPermissionWithDebug>
    </RestrictedByCapabilityWithDebug>
  );

  const switchViewButton = (
    <SwitchCollectionViewButton<TemplatesCollectionView>
      allowedViews={ALLOWED_VIEWS}
      activeView={activeView}
      setActiveView={setActiveView}
    />
  );

  const quickFilterTabs =
    activeQuickFilterOptions.length > 1 ? (
      <Tabs
        condensed
        useSelectOnMobile
        options={activeQuickFilterOptions}
        value={quickFilter}
        onChange={(newQuickFilter: QuickFilterValue) => {
          setQuickFilter(newQuickFilter);
        }}
      />
    ) : undefined;

  const filterButton = (
    <ModalOpenButton
      Modal={TemplateQuotesFilterModal}
      modalProps={{
        resetFilters,
        sorting,
        setSorting,
        filters,
        setFilters,
        showCreatedByFilter: section !== "MEISTER_STORE",
      }}
    >
      <FilterButton />
    </ModalOpenButton>
  );

  const searchField = (
    <DebouncedSearchInput
      placeholder={t("Search", { ns: "Global" })}
      defaultValue={searchTerm}
      onChangeSearchTerm={newValue => setSearchTerm(newValue)}
    />
  );

  const activeFilterChips = useTemplateDocumentsActiveFilterChips({
    filters,
    setFilters,
  });

  return (
    <Page
      title={sectionTitles[section]}
      subtitle={t("Document Templates", {
        ns: "Templates",
      })}
      submenuItems={submenuItems}
      action={actionButtons}
    >
      <PageContainer>
        <ListHeader
          CreateButton={createButton}
          MenuButton={importButton}
          QuickFilter={quickFilterTabs}
          FilterButton={filterButton}
          SwitchViewButton={switchViewButton}
          ActiveFilterChips={
            <ListHeaderActiveFilters
              activeFilterChips={activeFilterChips}
              collapsedContent={
                <Typography variant="body2">
                  {t("Active filters", { ns: "Global" })}
                  <Typography
                    variant="body2"
                    component="span"
                    marginLeft={1}
                    fontWeight="bold"
                  >
                    {t("{count} results", {
                      ns: "Global",
                      count: activeFilterChips.length,
                    })}
                  </Typography>
                </Typography>
              }
              clearAllButton={
                <ListHeaderActiveFiltersActionButton
                  label={t("Clear all", { ns: "Global" })}
                  onClick={resetFilters}
                />
              }
            />
          }
          SearchField={searchField}
          mb={2}
        />
        <TemplateDocumentsList
          items={items ?? []}
          totalCount={totalCount}
          loading={query.loading}
          pathToDocList={pathToDocList}
          activeView={activeView}
          sorting={sorting}
          setSorting={setSorting}
          paginationModel={paginationModel}
          setPaginationModel={setPaginationModel}
          selectedItems={selectedItems}
          setSelectedItems={setSelectedItems}
          showSubscribedColumn={section === "MEISTER_STORE"}
        />
      </PageContainer>
    </Page>
  );
};
